import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import {
  buildStageDetailPath,
  buildStageExecutionDetailPath,
  buildSyntheticStageId,
} from '@/features/pipelines/utils/stagePaths';
import { attachRowDailyHistoryRunLinks } from '@/features/shared/components/entityDailyHistory/dailyHistoryLinks';
import {
  buildDailyHistoryForReferenceDays,
  buildLatestDailyHistory,
} from '@/features/shared/components/entityDailyHistory/dailyHistoryRows';
import { resolveStageExecutionDisplayStatus } from '@/features/shared/utils/ciStatus';
import { buildJobDetailPath, buildJobRunDetailPath } from '../../jobs/utils/jobPaths';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistoryCell } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { PipelineListItemDto, PipelineStageExecutionListItemDto } from '@flowqa/shared';

const buildPipelineLatestDailyHistory = (pipeline: PipelineListItemDto): DailyHistoryCell[] => {
  const latestRun = pipeline.latestRun;
  if (!latestRun) {
    return [];
  }

  const latestRunKey = latestRun.buildKey ?? pipeline.runSummaries?.[0]?.buildKey ?? null;
  return buildLatestDailyHistory({
    timestamp: latestRun.timestamp,
    status: latestRun.status,
    detail: {
      status: latestRun.status,
      timestamp: latestRun.timestamp,
      runLabel: latestRunKey,
    },
  });
};

const getPipelineStageCount = (pipeline: PipelineListItemDto): number => pipeline.stageTotal ?? 0;

const getPipelineLoadingRowCount = (pipeline: PipelineListItemDto): number => {
  const stageCount = getPipelineStageCount(pipeline);
  if (stageCount <= 0) {
    return 2;
  }

  return Math.min(Math.max(stageCount, 2), 6);
};

const resolveStageExecutionTimestamp = (
  execution: PipelineStageExecutionListItemDto,
): Date | null => execution.lastUpdatedAt ?? execution.finishedAt ?? execution.startedAt ?? null;

type StageHistoryDetail = {
  status: NonNullable<DailyHistoryCell['status']>;
  timestamp: Date;
  runLabel: string | null;
  runHref: string;
};

type PipelineStageHistoryInfo = {
  name: string;
  href: string;
  sortOrder?: number;
  detailsByDay: Map<string, StageHistoryDetail[]>;
};

type JobHistoryDetail = {
  status: NonNullable<DailyHistoryCell['status']>;
  timestamp: Date;
  runLabel: string | null;
  runHref: string;
};

type PipelineJobHistoryInfo = {
  name: string;
  href: string;
  sortOrder?: number;
  detailsByDay: Map<string, JobHistoryDetail[]>;
};

const buildOrderedStageNames = (
  stageExecutions: readonly PipelineStageExecutionListItemDto[],
): Map<string, number> => {
  const latestExecutions = stageExecutions
    .filter((execution) => execution.isLatest)
    .slice()
    .sort((left, right) => {
      const leftTimestamp =
        resolveStageExecutionTimestamp(left)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const rightTimestamp =
        resolveStageExecutionTimestamp(right)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (leftTimestamp !== rightTimestamp) {
        return leftTimestamp - rightTimestamp;
      }

      return left.stageName.localeCompare(right.stageName, undefined, {
        sensitivity: 'base',
      });
    });

  return new Map(latestExecutions.map((execution, index) => [execution.stageName, index] as const));
};

const buildOrderedJobNamesByStageName = (
  stageExecutions: readonly PipelineStageExecutionListItemDto[],
): Map<string, Map<string, number>> => {
  const orderByStageName = new Map<string, Map<string, number>>();

  stageExecutions
    .filter((execution) => execution.isLatest)
    .forEach((execution) => {
      const jobOrderByName = orderByStageName.get(execution.stageName) ?? new Map<string, number>();
      const sortedJobs = [...(execution.jobSummaries ?? [])].sort((left, right) => {
        const leftTimestamp = left.timestamp?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const rightTimestamp = right.timestamp?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (leftTimestamp !== rightTimestamp) {
          return leftTimestamp - rightTimestamp;
        }

        return left.jobName.localeCompare(right.jobName, undefined, {
          sensitivity: 'base',
        });
      });

      sortedJobs.forEach((job) => {
        if (!jobOrderByName.has(job.jobName)) {
          jobOrderByName.set(job.jobName, jobOrderByName.size);
        }
      });

      orderByStageName.set(execution.stageName, jobOrderByName);
    });

  return orderByStageName;
};

const buildStageJobChildRows = (
  pipeline: PipelineListItemDto,
  stageName: string,
  stageExecutions: readonly PipelineStageExecutionListItemDto[],
  parentDailyHistory: DailyHistoryCell[],
  orderedJobNames: ReadonlyMap<string, number> | undefined,
): EntityDailyHistoryRow[] => {
  const jobInfoByName = new Map<string, PipelineJobHistoryInfo>();

  stageExecutions.forEach((execution) => {
    execution.jobSummaries?.forEach((jobSummary) => {
      const timestamp = jobSummary.timestamp;
      if (!(timestamp instanceof Date)) {
        return;
      }

      const dayKey = timestamp.toISOString().slice(0, 10);
      const existing = jobInfoByName.get(jobSummary.jobName) ?? {
        name: jobSummary.jobName,
        href: buildJobDetailPath({
          pipelineId: pipeline.id,
          stageName,
          jobName: jobSummary.jobName,
        }),
        sortOrder: orderedJobNames?.get(jobSummary.jobName),
        detailsByDay: new Map<string, JobHistoryDetail[]>(),
      };
      const details = existing.detailsByDay.get(dayKey) ?? [];
      details.push({
        status: jobSummary.status,
        timestamp,
        runLabel: jobSummary.buildKey ?? null,
        runHref: buildJobRunDetailPath({
          jobRunId: jobSummary.id,
          pipelineId: pipeline.id,
          stageName,
          jobName: jobSummary.jobName,
        }),
      });
      details.sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime());
      existing.detailsByDay.set(dayKey, details);
      jobInfoByName.set(jobSummary.jobName, existing);
    });
  });

  return Array.from(jobInfoByName.values()).map((jobInfo) => ({
    id: `${pipeline.id}-${stageName}-${jobInfo.name}`,
    name: jobInfo.name,
    href: jobInfo.href,
    sortOrder: jobInfo.sortOrder,
    dailyHistory: buildDailyHistoryForReferenceDays(parentDailyHistory, jobInfo.detailsByDay),
  }));
};

const buildPipelineStageChildRows = (
  pipeline: PipelineListItemDto,
  stageExecutions: PipelineStageExecutionListItemDto[] | undefined,
  parentDailyHistory: DailyHistoryCell[],
): EntityDailyHistoryRow[] => {
  const executions = stageExecutions ?? [];
  const orderedStageNames = buildOrderedStageNames(executions);
  const orderedJobNamesByStageName = buildOrderedJobNamesByStageName(executions);
  const stageInfoByName = new Map<string, PipelineStageHistoryInfo>();

  executions.forEach((execution) => {
    const timestamp = resolveStageExecutionTimestamp(execution);
    if (!timestamp) {
      return;
    }

    const dayKey = timestamp.toISOString().slice(0, 10);
    const existing = stageInfoByName.get(execution.stageName) ?? {
      name: execution.stageName,
      href: buildStageDetailPath(buildSyntheticStageId(pipeline.id, execution.stageName)),
      sortOrder: orderedStageNames.get(execution.stageName),
      detailsByDay: new Map<string, StageHistoryDetail[]>(),
    };
    const details = existing.detailsByDay.get(dayKey) ?? [];
    const displayStatus = resolveStageExecutionDisplayStatus(execution) ?? execution.status;
    details.push({
      status: displayStatus,
      timestamp,
      runLabel: execution.buildKey ?? null,
      runHref: buildStageExecutionDetailPath({
        stageExecutionId: execution.id,
        pipelineId: pipeline.id,
        stageName: execution.stageName,
      }),
    });
    details.sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime());
    existing.detailsByDay.set(dayKey, details);
    stageInfoByName.set(execution.stageName, existing);
  });

  return Array.from(stageInfoByName.values()).map((stageInfo) => ({
    id: `${pipeline.id}-${stageInfo.name}`,
    name: stageInfo.name,
    href: stageInfo.href,
    sortOrder: stageInfo.sortOrder,
    dailyHistory: buildDailyHistoryForReferenceDays(parentDailyHistory, stageInfo.detailsByDay),
    hasExpandableChildren: executions.some(
      (execution) => execution.stageName === stageInfo.name && (execution.jobTotal ?? 0) > 0,
    ),
    childRows: buildStageJobChildRows(
      pipeline,
      stageInfo.name,
      executions.filter((execution) => execution.stageName === stageInfo.name),
      parentDailyHistory,
      orderedJobNamesByStageName.get(stageInfo.name),
    ),
  }));
};

export const buildPipelineHistoryRows = (
  pipelines: PipelineListItemDto[] | undefined,
  stageExecutionsByPipelineId: ReadonlyMap<string, PipelineStageExecutionListItemDto[]>,
  expandedPipelineIds: ReadonlySet<string>,
  loadingPipelineIds: ReadonlySet<string>,
  isLatestMode: boolean,
): EntityDailyHistoryRow[] =>
  (pipelines ?? []).map((pipeline) => {
    const dailyHistory = isLatestMode
      ? buildPipelineLatestDailyHistory(pipeline)
      : (pipeline.dailyHistory ?? []);
    const row: EntityDailyHistoryRow = {
      id: pipeline.id,
      name: pipeline.name,
      href: `/pipelines/${resolvePipelineSlug(pipeline)}`,
      dailyHistory,
      hasExpandableChildren: getPipelineStageCount(pipeline) > 0,
      childrenLoading: loadingPipelineIds.has(pipeline.id),
      childrenLoadingRowCount: getPipelineLoadingRowCount(pipeline),
      childRows: expandedPipelineIds.has(pipeline.id)
        ? buildPipelineStageChildRows(
            pipeline,
            stageExecutionsByPipelineId.get(pipeline.id),
            dailyHistory,
          )
        : undefined,
    };

    return attachRowDailyHistoryRunLinks(row, 'pipeline', 'pipeline');
  });
